import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import { inr, dt, apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { useLocationFilter } from '../context/LocationContext';
import { useDataTable, TableToolbar, TableFilterRow, TablePager, useRowPick, PickedChip } from '../components/DataTable';
import { printBill, normBill } from '../components/BillDesign';
import Icon from '../components/Icon';
import HeaderSlot from '../components/HeaderSlot';

const channelBadge = (c) => ({ pos: 'badge-green', website: 'badge-blue', swiggy: 'badge-gold', zomato: 'badge-red', corporate: 'badge-blue', credit: 'badge-gray' }[c] || 'badge-gray');
const statusBadge = (s) => ({ completed: 'badge-green', returned: 'badge-red', partially_returned: 'badge-gold', cancelled: 'badge-gray' }[s] || 'badge-gray');

const SaleModal = ({ saleId, onClose, onChanged, can, billTemplate }) => {
  const [sale, setSale] = useState(null);
  const [taxSummary, setTax] = useState([]);
  const [ret, setRet] = useState({});           // saleItemId -> qty
  const [mode, setMode] = useState('cash');
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/sales/${saleId}`).then(({ data }) => { setSale(data.sale); setTax(data.taxSummary || []); }).catch((e) => toast.error(apiError(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [saleId]);

  const submitReturn = async () => {
    const items = Object.entries(ret).filter(([, q]) => Number(q) > 0).map(([saleItemId, quantity]) => ({ saleItemId: Number(saleItemId), quantity: Number(quantity) }));
    if (!items.length) return toast.error('Enter quantities to return');
    setBusy(true);
    try { const { data } = await api.post(`/sales/${saleId}/return`, { items, refundMode: mode }); toast.success(`Refunded ${inr(data.refundAmount)}`); setRet({}); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const cancelOrder = async () => {
    if (!window.confirm('Cancel this whole order? Every item will be restocked and the payments reversed.')) return;
    setBusy(true);
    try { const { data } = await api.post(`/sales/${saleId}/cancel`, {}); toast.success('Order cancelled — stock & books adjusted'); setSale(data.sale); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!sale) return <Modal title="Sale" onClose={onClose}><div className="spinner" /></Modal>;
  const canReturn = can('sales:return') && ['completed', 'partially_returned'].includes(sale.status);
  const canCancel = can('sales:return') && sale.status === 'completed';

  return (
    <Modal title={`${sale.saleNumber}`} onClose={onClose} wide
      footer={<>
        <button className="btn btn-outline" onClick={onClose}>Close</button>
        <button className="btn btn-outline" onClick={() => printBill(billTemplate, sale, taxSummary)}>Print bill</button>
        {canCancel && <button className="btn btn-danger" onClick={cancelOrder} disabled={busy}>Cancel order</button>}
        {canReturn && <><select value={mode} onChange={(e) => setMode(e.target.value)} style={{ width: 110 }}><option value="cash">Cash</option><option value="upi">UPI</option><option value="card">Card</option></select><button className="btn btn-danger" onClick={submitReturn} disabled={busy}>Process Return</button></>}
      </>}>
      <div className="flex" style={{ justifyContent: 'space-between' }}>
        <div><span className={`badge ${channelBadge(sale.channel)}`}>{sale.channel}</span> <span className={`badge ${statusBadge(sale.status)}`}>{sale.status.replace('_', ' ')}</span></div>
        <div className="muted">{dt(sale.createdAt)} · {sale.location?.name}</div>
      </div>
      <div className="table-wrap mt">
        <table className="grid">
          <thead><tr><th>Product</th><th className="right">Qty</th><th className="right">Returned</th><th className="right">Rate</th><th className="right">Amount</th>{canReturn && <th className="right">Return qty</th>}</tr></thead>
          <tbody>
            {sale.items.map((it) => {
              const remaining = it.quantity - (it.returnedQty || 0);
              return (
                <tr key={it.id}>
                  <td>{it.name} {it.packLabel}</td><td className="right">{it.quantity}</td><td className="right">{it.returnedQty || 0}</td>
                  <td className="right">{inr(it.unitPrice)}</td><td className="right">{inr(it.lineTotal)}</td>
                  {canReturn && <td><input type="number" min={0} max={remaining} style={{ width: 70 }} disabled={remaining <= 0} value={ret[it.id] || ''} onChange={(e) => setRet((p) => ({ ...p, [it.id]: e.target.value }))} /></td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt" style={{ maxWidth: 320, marginLeft: 'auto' }}>
        <div className="bill-row"><span>Taxable</span><span>{inr(sale.taxableAmount)}</span></div>
        {taxSummary.map((g) => <div className="bill-row muted" key={g.rate}><span>GST {g.rate}%</span><span>{inr(g.cgst + g.sgst)}</span></div>)}
        <div className="bill-row total"><span>Net</span><span>{inr(sale.netTotal)}</span></div>
        {(sale.payments || []).map((p) => <div className="bill-row muted" key={p.id}><span>{p.mode.toUpperCase()}</span><span>{inr(p.amount)}</span></div>)}
      </div>
    </Modal>
  );
};

// Settling a credit bill: the credit line shrinks, a real payment takes its
// place, and the cash lands in whichever register is open now.
const SettleModal = ({ sale, due, onClose, onSettled }) => {
  const [mode, setMode] = useState('cash');
  const [amount, setAmount] = useState(String(due));
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const amt = Number(amount) || 0;

  const settle = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/sales/${sale.id}/settle`, { mode, amount: amt, reference: reference.trim() || undefined });
      toast.success(data.outstanding > 0.005 ? `${inr(data.settled)} received · ${inr(data.outstanding)} still due` : 'Bill settled in full');
      onSettled(); onClose();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Settle ${sale.saleNumber}`} onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={settle} disabled={busy || !(amt > 0) || amt > due + 0.005}>{busy ? 'Saving…' : `Receive ${inr(amt)}`}</button></>}>
      <p className="muted small" style={{ marginTop: 0 }}>
        {sale.customer?.name ? <strong>{sale.customer.name}</strong> : 'This bill'}
        {sale.customer?.phone ? ` (${sale.customer.phone})` : ''} owes <strong>{inr(due)}</strong> on this bill.
      </p>
      <div className="flex gap" style={{ margin: '.2rem 0 .6rem' }}>
        {['cash', 'upi', 'card'].map((m) => (
          <button key={m} className={`pill-btn ${mode === m ? 'active' : ''}`} onClick={() => setMode(m)}>
            {m === 'upi' ? 'UPI' : m[0].toUpperCase() + m.slice(1)}
          </button>
        ))}
      </div>
      <div className="row">
        <div className="field" style={{ maxWidth: 180 }}><label>Amount received (₹)</label>
          <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </div>
        <div className="field"><label>Reference</label>
          <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UPI txn id / card auth — optional" />
        </div>
      </div>
      {amt > 0 && amt < due && <p className="muted small">{inr(due - amt)} will stay outstanding.</p>}
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

const Sales = () => {
  const { can } = useAuth();
  const { locationId } = useLocationFilter();   // top-level location filter
  const [sales, setSales] = useState([]);
  const [closing, setClosing] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [open, setOpen] = useState(null);
  const [settling, setSettling] = useState(null);
  const [billTemplate, setBillTemplate] = useState(() => normBill(null));

  // The printed bill's design, as set in Settings.
  useEffect(() => {
    api.get('/settings').then(({ data }) => setBillTemplate(normBill(data.settings && data.settings.billTemplate))).catch(() => {});
  }, []);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (locationId) q.set('locationId', locationId);
    if (date) q.set('date', date);   // one IST trading day, resolved server-side
    api.get(`/sales?${q}`).then(({ data }) => setSales(data.sales || [])).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
    const cq = new URLSearchParams({ date }); if (locationId) cq.set('locationId', locationId);
    api.get(`/sales/daily-closing?${cq}`).then(({ data }) => setClosing(data.summary)).catch(() => {});
  };
  useEffect(load, [locationId, date]); // eslint-disable-line

  const printRow = async (sale) => {
    try {
      const { data } = await api.get(`/sales/${sale.id}`);
      await printBill(billTemplate, data.sale, data.taxSummary || []);
    } catch (e) { toast.error(apiError(e)); }
  };

  const pick = useRowPick();
  const dtSales = useDataTable(sales, {
    searchPlaceholder: 'Search bill no., customer…',
    searchKeys: ['saleNumber', 'channel', 'status', (s) => s.customer?.name, (s) => s.customer?.phone],
    filters: [
      { key: 'channel', label: 'Channel', value: (s) => s.channel },
      { key: 'status', label: 'Status', value: (s) => s.status.replace('_', ' ') },
    ],
    // The boxes under the headings: type into the column you are looking at.
    columns: [
      { key: 'bill', label: 'Bill', value: (s) => s.saleNumber, placeholder: 'Bill no.' },
      { key: 'channel', label: 'Channel', value: (s) => s.channel, type: 'select' },
      { key: 'paidBy', label: 'Paid by', value: (s) => (s.payments || []).map((p) => p.mode).join(' '), placeholder: 'cash, upi…' },
      { key: 'status', label: 'Status', value: (s) => s.status.replace('_', ' '), type: 'select' },
    ],
  });

  // A credit line that has not been settled is what the customer still owes.
  const dueOf = (sale) => Math.round((sale.payments || [])
    .filter((p) => p.mode === 'credit').reduce((a, p) => a + Number(p.amount), 0) * 100) / 100;
  const returnedOf = (sale) => Number(sale.returned) || 0;
  const netOf = (sale) => (sale.netAfterReturns != null
    ? Number(sale.netAfterReturns)
    : Math.round(((Number(sale.netTotal) || 0) - returnedOf(sale)) * 100) / 100);

  const totals = dtSales.filtered.reduce((acc, s) => {
    acc.billed += Number(s.netTotal) || 0;
    acc.returned += returnedOf(s);
    acc.net += netOf(s);
    return acc;
  }, { billed: 0, returned: 0, net: 0 });
  const paidBy = (sale) => (sale.payments || []).map((p) => p.mode.toUpperCase()).join(' + ') || '—';

  return (
    <div>
      <HeaderSlot>
        <input className="topbar-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} title="Trading day" />
      </HeaderSlot>

      <div className="stat-strip">
        {closing && (<>
          <div className="strip-cell lead"><span className="k">Net</span><span className="v">{inr(closing.netAfterReturns != null ? closing.netAfterReturns : closing.net)}</span></div>
          <div className="strip-cell"><span className="k">Bills</span><span className="v">{closing.count}</span></div>
          {Number(closing.returns) > 0.005 && (
            <div className="strip-cell neg-v">
              <span className="k">Returned</span>
              <span className="v">−{inr(closing.returns)}</span>
              <span className="sub">({closing.returnCount} return{closing.returnCount === 1 ? '' : 's'})</span>
            </div>
          )}
          <div className="strip-cell"><span className="k">Cash</span><span className="v">{inr(closing.byMode.cash)}</span></div>
          <div className="strip-cell"><span className="k">UPI</span><span className="v">{inr(closing.byMode.upi)}</span></div>
          <div className="strip-cell"><span className="k">Card</span><span className="v">{inr(closing.byMode.card)}</span></div>
          {Number(closing.byMode.credit) > 0.005 && <div className="strip-cell"><span className="k">On credit</span><span className="v">{inr(closing.byMode.credit)}</span></div>}
          <div className="strip-cell"><span className="k">GST</span><span className="v">{inr(closing.cgst + closing.sgst)}</span></div>
        </>)}
      </div>

      {loading ? <div className="spinner" /> : (<>
        <TableToolbar dt={dtSales} right={<PickedChip pick={pick} />} />
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr><th className="freeze-col">Bill</th><th className="right">When</th><th>Channel</th><th>Paid by</th><th className="right">Billed</th><th className="right">Returned</th><th className="right">Net</th><th className="right">Due</th><th>Status</th><th></th></tr>
              <TableFilterRow dt={dtSales} cells={['bill', null, 'channel', 'paidBy', null, null, null, null, 'status', null]} />
            </thead>
            <tbody>
              {dtSales.rows.map((s) => (
                <tr key={s.id} {...pick.markProps(s.id)}>
                  <td {...pick.cellProps(s.id, 'mono freeze-col')}>{s.saleNumber}</td><td className="muted right">{dt(s.createdAt)}</td>
                  <td><span className={`badge ${channelBadge(s.channel)}`}>{s.channel}</span></td>
                  <td className="muted small">{paidBy(s)}</td>
                  <td className="right">{inr(s.netTotal)}</td>
                  <td className="right">{returnedOf(s) > 0.005 ? <span className="neg">−{inr(returnedOf(s))}</span> : '—'}</td>
                  <td className="right"><strong>{inr(netOf(s))}</strong></td>
                  <td className="right">{dueOf(s) > 0.005 ? <strong className="neg">{inr(dueOf(s))}</strong> : '—'}</td>
                  <td><span className={`badge ${statusBadge(s.status)}`}>{s.status.replace('_', ' ')}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {dueOf(s) > 0.005 && can('sales:create')
                      && <><button className="btn btn-primary btn-sm" onClick={() => setSettling(s)}>Settle</button>{' '}</>}
                    <button className="btn btn-outline btn-sm icon-btn" title={`Print ${s.saleNumber}`} aria-label="Print bill"
                      onClick={() => printRow(s)}><Icon name="printer" size={15} /></button>{' '}
                    <button className="btn btn-outline btn-sm icon-btn" title={`Open ${s.saleNumber}`} aria-label="Open bill"
                      onClick={() => setOpen(s.id)}><Icon name="open" size={15} /></button>
                  </td>
                </tr>
              ))}
              {dtSales.rows.length === 0 && <tr><td colSpan={10} className="empty">{dtSales.isFiltered ? 'No sales match your search.' : 'No sales.'}</td></tr>}
            </tbody>
            {dtSales.filtered.length > 0 && (
              <tfoot>
                <tr className="total-row">
                  <td colSpan={4}><strong>Total — {dtSales.filtered.length} bill{dtSales.filtered.length === 1 ? '' : 's'}{dtSales.isFiltered ? ' (filtered)' : ''}</strong></td>
                  <td className="right">{inr(totals.billed)}</td>
                  <td className="right">{totals.returned > 0.005 ? <span className="neg">−{inr(totals.returned)}</span> : '—'}</td>
                  <td className="right"><strong>{inr(totals.net)}</strong></td>
                  <td className="right">{dtSales.filtered.reduce((a, x) => a + dueOf(x), 0) > 0.005
                    ? <strong className="neg">{inr(dtSales.filtered.reduce((a, x) => a + dueOf(x), 0))}</strong> : '—'}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <TablePager dt={dtSales} />
      </>)}
      {open && <SaleModal saleId={open} can={can} billTemplate={billTemplate} onClose={() => setOpen(null)} onChanged={load} />}
      {settling && <SettleModal sale={settling} due={dueOf(settling)} onClose={() => setSettling(null)} onSettled={load} />}
    </div>
  );
};

export default Sales;
